import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Users, Crown, Medal, Star, Award, ChevronRight, Sparkles, Target, Shield } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

interface ClubTrophy {
  id: string;
  title: string;
  description: string | null;
  trophy_type: string;
  position: string | null;
  season: string | null;
  year: number | null;
  is_featured: boolean;
}

interface PlayerTrophy {
  id: string;
  player_id: string;
  title: string;
  description: string | null;
  trophy_type: string;
  season: string | null;
  year: number | null;
  player?: { id: string; name: string; avatar: string | null; slug: string } | null;
}

interface Tournament {
  id: string;
  name: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
  champion_id: string | null;
  runner_up_id: string | null;
  third_place_id: string | null;
  champion?: { id: string; name: string; avatar: string | null; slug: string } | null;
  runner_up?: { id: string; name: string; avatar: string | null; slug: string } | null;
  third_place?: { id: string; name: string; avatar: string | null; slug: string } | null;
}

interface ClubStats {
  totalTournaments: number;
  completedTournaments: number;
  totalMatches: number;
  totalWins: number;
  totalGoals: number;
  externalWins: number;
  externalMatches: number;
}

export function VirtualTrophyHouse() {
  const [isOpen, setIsOpen] = useState(false);
  const [clubTrophies, setClubTrophies] = useState<ClubTrophy[]>([]);
  const [playerTrophies, setPlayerTrophies] = useState<PlayerTrophy[]>([]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [clubStats, setClubStats] = useState<ClubStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [rotationAngle, setRotationAngle] = useState(0);
  const [isLocked, setIsLocked] = useState<boolean | null>(null);

  // Check if trophy house is locked
  useEffect(() => {
    const checkLockStatus = async () => {
      const { data } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'virtual_trophy_house_locked')
        .maybeSingle();
      // If no setting found, default to unlocked (false)
      setIsLocked(data?.value === true);
    };
    checkLockStatus();
  }, []);

  // Continuous rotation animation for trophies
  useEffect(() => {
    const interval = setInterval(() => {
      setRotationAngle(prev => (prev + 1) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch club trophies from new table
      const { data: clubTrophiesData } = await supabase
        .from('club_trophies')
        .select('*')
        .order('is_featured', { ascending: false })
        .order('display_order')
        .order('year', { ascending: false });

      if (clubTrophiesData) {
        setClubTrophies(clubTrophiesData);
      }

      // Fetch player trophies from new table
      const { data: playerTrophiesData } = await supabase
        .from('player_trophies')
        .select('*')
        .order('display_order')
        .order('year', { ascending: false });

      if (playerTrophiesData) {
        // Get player info
        const playerIds = [...new Set(playerTrophiesData.map(t => t.player_id))];
        const { data: playersData } = await supabase
          .from('players')
          .select('id, name, avatar, slug')
          .in('id', playerIds);

        const playerMap = new Map(playersData?.map(p => [p.id, p]) || []);

        setPlayerTrophies(playerTrophiesData.map(t => ({
          ...t,
          player: playerMap.get(t.player_id) || null,
        })));
      }

      // Fetch all tournaments with winners
      const { data: tournamentsData } = await supabase
        .from('tournaments')
        .select('id, name, status, start_date, end_date, champion_id, runner_up_id, third_place_id')
        .order('end_date', { ascending: false });

      if (tournamentsData) {
        const playerIds = new Set<string>();
        tournamentsData.forEach(t => {
          if (t.champion_id) playerIds.add(t.champion_id);
          if (t.runner_up_id) playerIds.add(t.runner_up_id);
          if (t.third_place_id) playerIds.add(t.third_place_id);
        });

        const { data: players } = await supabase
          .from('players')
          .select('id, name, avatar, slug')
          .in('id', Array.from(playerIds));

        const playerMap = new Map(players?.map(p => [p.id, p]) || []);

        setTournaments(tournamentsData.map(t => ({
          ...t,
          champion: t.champion_id ? playerMap.get(t.champion_id) || null : null,
          runner_up: t.runner_up_id ? playerMap.get(t.runner_up_id) || null : null,
          third_place: t.third_place_id ? playerMap.get(t.third_place_id) || null : null,
        })));
      }

      // Fetch club stats
      const [
        { count: totalTournaments },
        { count: completedTournaments },
        { count: totalMatches },
        { data: leaderboardStats },
        { data: externalData },
      ] = await Promise.all([
        supabase.from('tournaments').select('id', { count: 'exact', head: true }),
        supabase.from('tournaments').select('id', { count: 'exact', head: true }).eq('status', 'completed'),
        supabase.from('matches').select('id', { count: 'exact', head: true }),
        supabase.from('leaderboard').select('wins, goals_for').eq('period_type', 'all_time'),
        supabase.from('external_matches').select('id, score_vsu, score_opponent'),
      ]);

      const totalWins = leaderboardStats?.reduce((sum, l) => sum + (l.wins || 0), 0) || 0;
      const totalGoals = leaderboardStats?.reduce((sum, l) => sum + (l.goals_for || 0), 0) || 0;
      const externalWins = externalData?.filter(m => (m.score_vsu || 0) > (m.score_opponent || 0)).length || 0;

      setClubStats({
        totalTournaments: totalTournaments || 0,
        completedTournaments: completedTournaments || 0,
        totalMatches: totalMatches || 0,
        totalWins,
        totalGoals,
        externalWins,
        externalMatches: externalData?.length || 0,
      });

    } catch (error) {
      console.error('Error fetching trophy data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPositionEmoji = (position: string | null) => {
    const map: Record<string, string> = {
      champion: '🏆',
      runner_up: '🥈',
      third: '🥉',
      participant: '🏅',
    };
    return map[position || 'champion'] || '🏆';
  };

  const getTrophyTypeEmoji = (type: string) => {
    const map: Record<string, string> = {
      award: '🏆',
      potw: '⭐',
      potm: '🌟',
      golden_boot: '👟',
      golden_glove: '🧤',
      mvp: '🎖️',
      special: '💎',
    };
    return map[type] || '🏆';
  };

  const featuredTrophies = clubTrophies.filter(t => t.is_featured);
  const allTrophiesForCarousel = clubTrophies.length > 0 ? clubTrophies : [];

  // Don't render if locked or still checking
  if (isLocked === null || isLocked === true) {
    return null;
  }

  return (
    <>
      {/* Trophy House Entry Card */}
      <section className="py-6">
        <div className="container">
          <button
            onClick={() => setIsOpen(true)}
            className="w-full group relative overflow-hidden rounded-2xl bg-gradient-to-br from-gold/20 via-amber-900/30 to-yellow-900/20 border border-gold/30 p-6 transition-all duration-500 hover:scale-[1.02] hover:border-gold/50 hover:shadow-[0_0_60px_rgba(255,215,0,0.3)]"
          >
            {/* Animated background */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,215,0,0.15),transparent_70%)] animate-pulse" />
            
            {/* Floating trophies */}
            <div className="absolute top-4 right-4 text-4xl animate-bounce" style={{ animationDelay: '0s' }}>🏆</div>
            <div className="absolute top-12 right-16 text-2xl animate-bounce" style={{ animationDelay: '0.3s' }}>🥇</div>
            <div className="absolute bottom-4 right-8 text-3xl animate-bounce" style={{ animationDelay: '0.6s' }}>🏅</div>
            
            <div className="relative z-10 flex items-center gap-4">
              <div className="p-4 rounded-2xl bg-gradient-to-br from-gold/30 to-amber-600/20 border border-gold/40 shadow-[0_0_30px_rgba(255,215,0,0.2)]">
                <Trophy className="h-10 w-10 text-gold" />
              </div>
              <div className="flex-1 text-left">
                <h2 className="font-display text-2xl text-gold flex items-center gap-2">
                  Virtual Trophy House
                  <Sparkles className="h-5 w-5 text-amber-400 animate-pulse" />
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  ক্লাব ও প্লেয়ারদের সকল অর্জন • টুর্নামেন্ট হিস্ট্রি • ৩৬০° ট্রফি ভিউ
                </p>
              </div>
              <ChevronRight className="h-6 w-6 text-gold group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        </div>
      </section>

      {/* Trophy House Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0 bg-gradient-to-b from-card via-card to-background border-gold/30">
          <DialogHeader className="p-6 pb-0 border-b border-border/50 bg-gradient-to-r from-gold/10 via-transparent to-gold/10">
            <DialogTitle className="flex items-center gap-3 text-2xl">
              <div className="p-2 rounded-xl bg-gold/20 border border-gold/30">
                <Trophy className="h-6 w-6 text-gold" />
              </div>
              <span className="bg-gradient-to-r from-gold via-amber-400 to-yellow-500 bg-clip-text text-transparent font-display">
                Virtual Trophy House
              </span>
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="club" className="flex-1">
            <div className="px-6 pt-4">
              <TabsList className="grid w-full grid-cols-2 bg-muted/50">
                <TabsTrigger value="club" className="data-[state=active]:bg-gold/20 data-[state=active]:text-gold">
                  <Shield className="h-4 w-4 mr-2" />
                  ক্লাব অর্জন
                </TabsTrigger>
                <TabsTrigger value="players" className="data-[state=active]:bg-gold/20 data-[state=active]:text-gold">
                  <Users className="h-4 w-4 mr-2" />
                  প্লেয়ার অর্জন
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <Trophy className="h-12 w-12 text-gold mx-auto animate-bounce" />
                    <p className="text-muted-foreground mt-4">ট্রফি লোড হচ্ছে...</p>
                  </div>
                </div>
              ) : (
                <>
                  <TabsContent value="club" className="mt-0 space-y-6">
                    {/* Club Stats Summary */}
                    {clubStats && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <StatCard icon={Trophy} label="মোট টুর্নামেন্ট" value={clubStats.totalTournaments} color="gold" />
                        <StatCard icon={Crown} label="সম্পন্ন" value={clubStats.completedTournaments} color="amber" />
                        <StatCard icon={Target} label="মোট ম্যাচ" value={clubStats.totalMatches} color="blue" />
                        <StatCard icon={Star} label="মোট গোল" value={clubStats.totalGoals} color="green" />
                      </div>
                    )}

                    {/* 3D Trophy Showcase */}
                    <div className="relative py-8">
                      <h3 className="text-lg font-display text-center mb-6 text-gold">🏆 ট্রফি শোকেস</h3>
                      
                      <div className="relative h-64 perspective-1000">
                        <div 
                          className="absolute inset-0 flex items-center justify-center"
                          style={{
                            transform: `rotateY(${rotationAngle}deg)`,
                            transformStyle: 'preserve-3d',
                            transition: 'transform 0.05s linear',
                          }}
                        >
                          {allTrophiesForCarousel.slice(0, 8).map((trophy, index) => {
                            const angle = (360 / Math.min(allTrophiesForCarousel.length || 1, 8)) * index;
                            const radius = 120;
                            const x = Math.sin((angle * Math.PI) / 180) * radius;
                            const z = Math.cos((angle * Math.PI) / 180) * radius;
                            
                            return (
                              <div
                                key={trophy.id}
                                className="absolute flex flex-col items-center"
                                style={{
                                  transform: `translateX(${x}px) translateZ(${z}px) rotateY(${-rotationAngle}deg)`,
                                  transformStyle: 'preserve-3d',
                                }}
                              >
                                <div className="text-5xl drop-shadow-[0_0_15px_rgba(255,215,0,0.5)]">{getPositionEmoji(trophy.position)}</div>
                                <div className="mt-2 text-xs text-center text-gold/80 font-medium max-w-20 truncate">
                                  {trophy.title}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Tournament History */}
                    <div>
                      <h3 className="text-lg font-display mb-4 flex items-center gap-2">
                        <Medal className="h-5 w-5 text-gold" />
                        টুর্নামেন্ট হিস্ট্রি
                      </h3>
                      
                      <div className="space-y-3">
                        {tournaments.map((tournament) => (
                          <div
                            key={tournament.id}
                            className={`p-4 rounded-xl border transition-all ${
                              tournament.status === 'completed'
                                ? 'bg-gradient-to-r from-gold/10 to-transparent border-gold/30'
                                : tournament.status === 'active'
                                ? 'bg-gradient-to-r from-green-500/10 to-transparent border-green-500/30'
                                : 'bg-muted/30 border-border/50'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">
                                    {tournament.status === 'completed' ? '🏆' : tournament.status === 'active' ? '🔴' : '📅'}
                                  </span>
                                  <h4 className="font-medium">{tournament.name}</h4>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    tournament.status === 'completed' ? 'bg-gold/20 text-gold' :
                                    tournament.status === 'active' ? 'bg-green-500/20 text-green-400' :
                                    'bg-muted text-muted-foreground'
                                  }`}>
                                    {tournament.status === 'completed' ? 'সম্পন্ন' : 
                                     tournament.status === 'active' ? 'চলমান' : 'আসন্ন'}
                                  </span>
                                </div>
                                
                                {tournament.start_date && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {format(new Date(tournament.start_date), 'MMM yyyy')}
                                    {tournament.end_date && ` - ${format(new Date(tournament.end_date), 'MMM yyyy')}`}
                                  </p>
                                )}
                              </div>
                            </div>

                            {tournament.status === 'completed' && (tournament.champion || tournament.runner_up || tournament.third_place) && (
                              <div className="mt-3 pt-3 border-t border-border/30 grid grid-cols-3 gap-2">
                                {tournament.champion && (
                                  <WinnerBadge 
                                    position="🥇" 
                                    player={tournament.champion} 
                                    label="চ্যাম্পিয়ন" 
                                  />
                                )}
                                {tournament.runner_up && (
                                  <WinnerBadge 
                                    position="🥈" 
                                    player={tournament.runner_up} 
                                    label="রানার্স আপ" 
                                  />
                                )}
                                {tournament.third_place && (
                                  <WinnerBadge 
                                    position="🥉" 
                                    player={tournament.third_place} 
                                    label="৩য় স্থান" 
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        ))}

                        {tournaments.length === 0 && (
                          <div className="text-center py-8 text-muted-foreground">
                            কোন টুর্নামেন্ট পাওয়া যায়নি
                          </div>
                        )}
                      </div>
                    </div>

                    {/* External Matches Stats */}
                    {clubStats && clubStats.externalMatches > 0 && (
                      <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-accent/10 to-transparent border border-accent/30">
                        <h3 className="text-lg font-display mb-3 flex items-center gap-2">
                          <Shield className="h-5 w-5 text-accent" />
                          এক্সটার্নাল ম্যাচ (ক্লাব vs ক্লাব)
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-accent">{clubStats.externalMatches}</div>
                            <div className="text-xs text-muted-foreground">মোট ম্যাচ</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-400">{clubStats.externalWins}</div>
                            <div className="text-xs text-muted-foreground">জয়</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="players" className="mt-0 space-y-6">
                    <h3 className="text-lg font-display flex items-center gap-2">
                      <Award className="h-5 w-5 text-gold" />
                      প্লেয়ার অর্জন সমূহ
                    </h3>

                    {playerTrophies.length > 0 ? (
                      <div className="space-y-3">
                        {playerTrophies.map((trophy, index) => (
                          <Link
                            key={trophy.id}
                            to={`/players/${trophy.player?.slug || ''}`}
                            className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-card to-transparent border border-border/50 hover:border-gold/30 transition-all group"
                          >
                            <div className="text-2xl font-bold text-muted-foreground w-8">
                              #{index + 1}
                            </div>
                            <div className="h-12 w-12 rounded-full overflow-hidden border-2 border-gold/30">
                              {trophy.player?.avatar ? (
                                <img src={trophy.player.avatar} alt={trophy.player.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full bg-muted flex items-center justify-center text-lg font-bold">
                                  {trophy.player?.name?.charAt(0) || '?'}
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium group-hover:text-gold transition-colors">
                                {trophy.player?.name || 'Unknown'}
                              </h4>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-lg">{getTrophyTypeEmoji(trophy.trophy_type)}</span>
                                <span className="text-sm text-muted-foreground">{trophy.title}</span>
                                {trophy.season && <span className="text-xs text-gold/70">{trophy.season}</span>}
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        কোন প্লেয়ার অর্জন পাওয়া যায়নি
                      </div>
                    )}
                  </TabsContent>
                </>
              )}
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatCard({ icon: Icon, label, value, color }: { icon: any; label: string; value: number; color: string }) {
  const colorClasses = {
    gold: 'from-gold/20 to-amber-600/10 border-gold/30 text-gold',
    amber: 'from-amber-500/20 to-orange-600/10 border-amber-500/30 text-amber-400',
    blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400',
    green: 'from-green-500/20 to-green-600/10 border-green-500/30 text-green-400',
  }[color] || 'from-muted to-muted/50 border-border text-foreground';

  return (
    <div className={`p-4 rounded-xl bg-gradient-to-br ${colorClasses} border text-center`}>
      <Icon className="h-6 w-6 mx-auto mb-2" />
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs opacity-80">{label}</div>
    </div>
  );
}

function WinnerBadge({ position, player, label }: { position: string; player: { name: string; avatar: string | null; slug: string }; label: string }) {
  return (
    <Link
      to={`/players/${player.slug}`}
      className="flex items-center gap-2 p-2 rounded-lg bg-background/50 hover:bg-background/80 transition-colors"
    >
      <span className="text-lg">{position}</span>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium truncate">{player.name}</div>
        <div className="text-[10px] text-muted-foreground">{label}</div>
      </div>
    </Link>
  );
}
